import { useState } from 'react';
import type { Player, BoardCell, ReverseCardId, TurnPhase } from '../../types';
import { REVERSE_CARDS, CHARACTERS } from '../../data';
import { getCharPortraitPath } from '../../data/assets';
import styles from './CardHand.module.css';

interface Props {
  player: Player;
  onUseCard: (cardId: string, targetId?: string, extra?: any) => void;
  players: Player[];
  board: BoardCell[];
  turnPhase: TurnPhase;
  isMobile?: boolean;
  isPortrait?: boolean;
  /** 联机模式：当前是否轮到本地玩家操作 */
  isMyTurn?: boolean;
}

export default function CardHand({ player, onUseCard, players, board, turnPhase, isMobile, isPortrait, isMyTurn }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [guessNumber, setGuessNumber] = useState(3);
  const [navSteps, setNavSteps] = useState(3);

  const cards = player.reverseCards.map(id => REVERSE_CARDS.find(c => c.id === id)).filter(Boolean);

  // 只有轮到该玩家操作时才能使用卡牌，否则只能查看
  const canUse = (turnPhase === 'rolling' || turnPhase === 'action' || turnPhase === 'buyDecision') && isMyTurn !== false;

  const handleCardClick = (cardId: string) => {
    if (!canUse) return;
    setSelectedCard(selectedCard === cardId ? null : cardId);
    setSelectedTarget(null);
  };

  const handleUse = () => {
    if (!selectedCard) return;

    let extra: any = undefined;
    if (selectedCard === '乾坤一掷') {
      extra = { guess: guessNumber };
    } else if (selectedCard === '精准导航') {
      extra = { steps: navSteps };
    } else if (selectedCard === '拆迁令' && selectedTarget) {
      // Find the street of target's most upgraded property
      const targetPlayer = players.find(p => p.id === selectedTarget);
      if (targetPlayer && targetPlayer.properties.length > 0) {
        const firstProp = targetPlayer.properties[0];
        const cell = board[firstProp];
        extra = { street: cell?.street };
      }
    }

    onUseCard(selectedCard, selectedTarget ?? undefined, extra);
    setSelectedCard(null);
    setSelectedTarget(null);
  };

  if (cards.length === 0) return null;

  return (
    <div className={`${styles.container} ${expanded ? styles.expanded : ''} ${isMobile ? styles.mobile : ''} ${isPortrait ? styles.portrait : ''}`}>
      {/* 收起时的图标条 */}
      {!expanded && (
        <button
          className={styles.toggleBtn}
          onClick={() => setExpanded(true)}
        >
          <span>🃏 手牌 ×{cards.length}</span>
          <span className={styles.toggleArrow}>▲</span>
        </button>
      )}

      {/* 展开的手牌 */}
      {expanded && (
        <div className={styles.expandedPanel}>
          <div className={styles.header}>
            <span className={styles.headerTitle}>你的手牌 ({cards.length}/3)</span>
            <button className={styles.closeBtn} onClick={() => { setExpanded(false); setSelectedCard(null); }}>
              ▼ 收起
            </button>
          </div>

          <div className={styles.cardList}>
            {cards.map(card => card && (
              <div
                key={card.id}
                className={`${styles.card} ${selectedCard === card.id ? styles.cardSelected : ''}`}
                onClick={() => handleCardClick(card.id)}
              >
                <img
                  src="/assets/cards/card_back_reverse.png"
                  alt={card.name}
                  className={styles.cardImg}
                />
                <div className={styles.cardOverlay}>
                  <span className={styles.cardEmoji}>{card.emoji}</span>
                  <span className={styles.cardName}>{card.name}</span>
                  {selectedCard === card.id && (
                    <div className={styles.cardPreview}>
                      <div className={styles.cardPreviewDesc}>{card.description}</div>
                      {card.cost !== undefined && (
                        <div className={styles.cardPreviewCost}>💰 消耗 ¥{card.cost}</div>
                      )}
                      {card.canTarget && (
                        <div className={styles.cardPreviewTarget}>🎯 需要目标玩家</div>
                      )}

                      {/* ====== 操作区直接放在选中卡片下方 ====== */}
                      {(() => {
                        if (selectedCard === '强制联盟' || selectedCard === '拆迁令') {
                          return (
                            <div className={styles.inlineTargetSelect}>
                              <span className={styles.inlineTargetLabel}>选择目标:</span>
                              <div className={styles.inlineTargetList}>
                                {players.filter(p => p.id !== player.id && !p.bankrupt).map(p => (
                                  <button
                                    key={p.id}
                                    className={`${styles.inlineTargetBtn} ${selectedTarget === p.id ? styles.inlineTargetActive : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setSelectedTarget(p.id); }}
                                  >
                                    <img
                                      src={getCharPortraitPath(p.characterId)}
                                      alt={p.name}
                                      className={styles.inlineTargetAvatar}
                                    />
                                    <span>{p.name}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        if (selectedCard === '乾坤一掷') {
                          return (
                            <div className={styles.inlineGuessSelect}>
                              <span className={styles.inlineTargetLabel}>猜骰子点数(1-6):</span>
                              <div className={styles.inlineGuessGrid}>
                                {[1, 2, 3, 4, 5, 6].map(n => (
                                  <button
                                    key={n}
                                    className={`${styles.inlineGuessBtn} ${guessNumber === n ? styles.inlineGuessActive : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setGuessNumber(n); }}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        if (selectedCard === '精准导航') {
                          return (
                            <div className={styles.inlineGuessSelect}>
                              <span className={styles.inlineTargetLabel}>选择步数(1-6):</span>
                              <div className={styles.inlineGuessGrid}>
                                {[1, 2, 3, 4, 5, 6].map(n => (
                                  <button
                                    key={n}
                                    className={`${styles.inlineGuessBtn} ${navSteps === n ? styles.inlineGuessActive : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setNavSteps(n); }}
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })()}

                      <button
                        className={styles.inlineUseBtn}
                        onClick={(e) => { e.stopPropagation(); handleUse(); }}
                        disabled={!canUse}
                      >
                        {canUse ? `使用${card.name}` : '等待轮到你操作'}
                      </button>
                    </div>
                  )}
                </div>
                {selectedCard === card.id && <div className={styles.cardGlow} />}
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}

function CHAR_EMOJI(charId: string): string {
  const map: Record<string, string> = {
    zhaocaimao: '🐱', fox: '🦊', eagle: '🦅', turtle: '🐢',
    rabbit: '🐰', dragon: '🐲', owl: '🦉', wolf: '🐺',
  };
  return map[charId] || '🎭';
}
